/**
 * BOM (Bill of Materials) Panel
 *
 * Real-time pricing and material list for the kitchen design.
 * Displays:
 * - All placed products with quantities
 * - Individual and total pricing
 * - Export options (PDF/CSV)
 *
 * Based on BOM.md specification for commercial document generation.
 */

import { useMemo, useCallback } from "react";
import { useFloorPlanStore } from "@/stores/floorPlan";
import { cn } from "@/lib/utils";
import type { ProductResponse, PlacedVolume } from "@byk/shared";

// =============================================================================
// Types
// =============================================================================

interface BOMLineItem {
  product: ProductResponse;
  quantity: number;
  volumeIds: string[];
  lineTotal: number;
}

interface BOMSummary {
  totalItems: number;
  uniqueProducts: number;
  subtotal: number;
  currency: string;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Group placed volumes by product and calculate quantities
 */
function calculateBOMItems(
  volumes: PlacedVolume[],
  products: ProductResponse[]
): BOMLineItem[] {
  // Group volumes by product ID
  const productGroups = new Map<string, string[]>();

  for (const volume of volumes) {
    const existing = productGroups.get(volume.productId) || [];
    existing.push(volume.id);
    productGroups.set(volume.productId, existing);
  }

  // Create line items
  const items: BOMLineItem[] = [];

  for (const [productId, volumeIds] of productGroups) {
    const product = products.find((p) => p.id === productId);
    if (!product) continue;

    const quantity = volumeIds.length;
    const lineTotal = (product.price || 0) * quantity;

    items.push({
      product,
      quantity,
      volumeIds,
      lineTotal,
    });
  }

  // Sort by category, then by name
  items.sort((a, b) => {
    if (a.product.categoryId !== b.product.categoryId) {
      return a.product.categoryId.localeCompare(b.product.categoryId);
    }
    return a.product.name.localeCompare(b.product.name);
  });

  return items;
}

/**
 * Calculate BOM summary totals
 */
function calculateSummary(items: BOMLineItem[]): BOMSummary {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const uniqueProducts = items.length;
  const subtotal = items.reduce((sum, item) => sum + item.lineTotal, 0);
  const currency = items[0]?.product.currency || "ILS";

  return {
    totalItems,
    uniqueProducts,
    subtotal,
    currency,
  };
}

/**
 * Get category icon
 */
function getCategoryIcon(categoryId: string): string {
  const icons: Record<string, string> = {
    sinks: "🚰",
    taps: "🚿",
    ovens: "🔥",
    cooktops: "♨️",
    surfaces: "🪨",
    cabinets: "🗄️",
    purifiers: "💧",
  };
  return icons[categoryId] || "📦";
}

/**
 * Format currency
 */
function formatCurrency(amount: number, currency: string): string {
  return `${amount.toLocaleString()} ${currency}`;
}

// =============================================================================
// Components
// =============================================================================

interface BOMLineItemRowProps {
  item: BOMLineItem;
  onSelect: (volumeId: string) => void;
}

const BOMLineItemRow: React.FC<BOMLineItemRowProps> = ({ item, onSelect }) => {
  const { product, quantity, volumeIds, lineTotal } = item;
  const dimensions = product.dimensions;

  return (
    <div
      className={cn(
        "group rounded-lg border border-gray-100 bg-white p-3 transition-all",
        "hover:border-blue-200 hover:shadow-sm"
      )}
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-gray-100 to-gray-50 text-lg">
          {getCategoryIcon(product.categoryId)}
        </div>

        {/* Product info */}
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium text-gray-900">{product.name}</h4>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span className="font-mono">{product.sku}</span>
            {dimensions && (
              <span>
                {dimensions.width}×{dimensions.depth}×{dimensions.height} cm
              </span>
            )}
          </div>
        </div>

        {/* Quantity badge */}
        <div className="flex flex-col items-end gap-1">
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
            ×{quantity}
          </span>
        </div>
      </div>

      {/* Price row */}
      <div className="mt-2 flex items-center justify-between border-t border-gray-50 pt-2">
        <div className="text-xs text-gray-400">
          {product.price ? (
            <span>
              {formatCurrency(product.price, product.currency || "ILS")} each
            </span>
          ) : (
            <span className="italic">Price TBD</span>
          )}
        </div>

        <div className="text-sm font-semibold text-gray-900">
          {lineTotal > 0 ? (
            formatCurrency(lineTotal, product.currency || "ILS")
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </div>
      </div>

      {/* Selection hint (on hover) */}
      {volumeIds.length > 0 && (
        <div className="mt-2 hidden group-hover:block">
          <button
            onClick={() => onSelect(volumeIds[0])}
            className="text-xs text-blue-600 hover:underline"
          >
            Select in canvas →
          </button>
        </div>
      )}
    </div>
  );
};

interface BOMSummaryCardProps {
  summary: BOMSummary;
}

const BOMSummaryCard: React.FC<BOMSummaryCardProps> = ({ summary }) => {
  return (
    <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-blue-700">
        Quote Summary
      </h4>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Total Items</span>
          <span className="font-medium text-gray-900">{summary.totalItems}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Unique Products</span>
          <span className="font-medium text-gray-900">{summary.uniqueProducts}</span>
        </div>
        <div className="my-2 border-t border-blue-100" />
        <div className="flex justify-between">
          <span className="text-sm font-medium text-gray-700">Subtotal</span>
          <span className="text-lg font-bold text-blue-700">
            {formatCurrency(summary.subtotal, summary.currency)}
          </span>
        </div>
      </div>

      {/* VAT notice */}
      <p className="mt-3 text-[10px] text-gray-500">
        * Prices exclude VAT and installation costs
      </p>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

interface BOMPanelProps {
  className?: string;
}

export const BOMPanel: React.FC<BOMPanelProps> = ({ className }) => {
  const { volumes, products, select } = useFloorPlanStore();

  // Calculate BOM items and summary
  const bomItems = useMemo(
    () => calculateBOMItems(volumes, products),
    [volumes, products]
  );

  const summary = useMemo(() => calculateSummary(bomItems), [bomItems]);

  // Handle selecting an item in the canvas
  const handleSelectVolume = useCallback(
    (volumeId: string) => {
      select(volumeId);
    },
    [select]
  );

  // Export handlers (placeholder for future implementation)
  const handleExportPDF = useCallback(() => {
    // TODO: Implement PDF export via backend API
    console.log("Export PDF - BOM items:", bomItems);
    alert("PDF export coming soon!");
  }, [bomItems]);

  const handleExportCSV = useCallback(() => {
    // Generate CSV content
    const headers = ["SKU", "Product", "Category", "Dimensions", "Qty", "Unit Price", "Total"];
    const rows = bomItems.map((item) => [
      item.product.sku,
      item.product.name,
      item.product.categoryId,
      item.product.dimensions
        ? `${item.product.dimensions.width}x${item.product.dimensions.depth}x${item.product.dimensions.height}`
        : "",
      item.quantity.toString(),
      (item.product.price || 0).toString(),
      item.lineTotal.toString(),
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");

    // Download file
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `byk-bom-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [bomItems]);

  // Empty state
  if (bomItems.length === 0) {
    return (
      <div className={cn("p-4", className)}>
        <div className="flex flex-col items-center justify-center rounded-xl bg-gray-50 py-8 text-center">
          <div className="mb-3 text-4xl">📋</div>
          <h4 className="text-sm font-medium text-gray-700">No Items Yet</h4>
          <p className="mt-1 text-xs text-gray-500">
            Drag products onto the canvas to build your quote
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {/* Summary card */}
      <div className="p-3">
        <BOMSummaryCard summary={summary} />
      </div>

      {/* Export buttons */}
      <div className="flex gap-2 px-3 pb-3">
        <button
          onClick={handleExportPDF}
          className={cn(
            "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2",
            "bg-blue-600 text-sm font-medium text-white",
            "hover:bg-blue-700 transition-colors"
          )}
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          PDF
        </button>
        <button
          onClick={handleExportCSV}
          className={cn(
            "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2",
            "bg-gray-100 text-sm font-medium text-gray-700",
            "hover:bg-gray-200 transition-colors"
          )}
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          CSV
        </button>
      </div>

      {/* Line items */}
      <div className="flex-1 overflow-y-auto px-3 pb-3" style={{ overscrollBehaviorY: 'contain' }}>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
          Line Items
        </h4>
        <div className="space-y-2">
          {bomItems.map((item) => (
            <BOMLineItemRow
              key={item.product.id}
              item={item}
              onSelect={handleSelectVolume}
            />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-2">
        <p className="text-center text-[10px] text-gray-400">
          Real-time pricing from vendor catalog
        </p>
      </div>
    </div>
  );
};

export default BOMPanel;
